import Phaser from 'phaser';
import type { Board } from '../game/Board';
import type { Obstacle } from '../game/Obstacle';
import type { PuppyId } from '../game/types';
import { puppyForId } from '../game/Puppy';
import { pickIdleAnimKey } from './puppyAnimations';
import { PUPPY_ANCHORS, PUPPY_MAX_DIM } from './puppyAnchors.generated';
import { alignmentForStill, alignmentForAnim } from './puppyAlignmentOffsets';
import { Z } from './zOrder';
import { regions } from './layout';

interface SquareSprites {
  grass: Phaser.GameObjects.Image;
  color: Phaser.GameObjects.Rectangle;
  puppy: Phaser.GameObjects.Sprite;
}

// iOS Square.swift::grassClearedAlpha / grassNotClearedAlpha. Cleared cells
// fade the grass texture; the scene background (light green-white) shows
// through and gives the pale tan "dirt" look — no actual dirt sprite or tint.
const GRASS_CLEARED_ALPHA = 0.25;
const GRASS_NOT_CLEARED_ALPHA = 1.0;

// Hex values sampled from iOS squaresbevel/color{N}@2x.png.
const COLOR_HEX: Record<string, number> = {
  red: 0xea0714,
  yellow: 0xffe600,
  orange: 0xfd8900,
  blue: 0x0024ff,
  green: 0x03da18,
  pink: 0xe6498e,
  purple: 0x7203da,
};

interface MegaVisual {
  border: Phaser.GameObjects.Image;
  bg: Phaser.GameObjects.Image;
  sprite: Phaser.GameObjects.Sprite;
  idleTimer: Phaser.Time.TimerEvent;
  maskG: Phaser.GameObjects.Graphics;
}

export class BoardView {
  readonly container: Phaser.GameObjects.Container;
  private sprites: SquareSprites[] = [];
  // Track per-mega visuals so we can tear them down when a row/col clears
  // them off the board (or when a SuperMega is converted to a CrazySquare).
  private megaVisuals = new Map<number, MegaVisual>();
  // Bound pointerdown listener — kept around so destroy() can detach it.
  // Without this, replayCurrentLevel (which builds a new BoardView) leaves
  // the old listener attached and every tap fires both, producing a stray
  // whimper sfx as the old handler tries to place on an already-filled cell.
  private pointerHandler: (pointer: Phaser.Input.Pointer) => void;
  readonly cellWidth: number;
  readonly cellHeight: number;
  readonly originX: number;
  readonly originY: number;

  constructor(
    private scene: Phaser.Scene,
    private board: Board,
    onCellTap: (cellIndex: number) => void,
  ) {
    // Sentinel "empty" puppy texture — 1x1 fully transparent. Used as the
    // explicit "no puppy here" texture so empty cells can't leak a stale
    // breed if some bug ever flips visibility back to true. Also used as
    // the marker rerender's diagnostic check looks for.
    if (!scene.textures.exists('empty-puppy')) {
      const g = scene.make.graphics({ x: 0, y: 0 });
      g.generateTexture('empty-puppy', 1, 1);
      g.destroy();
    }

    const r = regions().board;
    // Cells stay square (cellH == cellW) and the cell grid is anchored to the
    // top-left of the board region so it flushes against the ground/trees
    // image above. Footer height should be sized so the cell grid lines up
    // with its top edge — no gutter on the sides, no leftover at the top.
    this.cellWidth = r.width / board.numSquaresHoriz;
    this.cellHeight = this.cellWidth;
    this.originX = r.x;
    this.originY = r.y;

    this.container = scene.add.container(0, 0);
    this.container.setDepth(Z.board);

    // Pale tan board backing so cleared cells (grass at 25% alpha) show as
    // dirt — mirrors iOS, where the scene background colour is light
    // green-white and shows through dimmed grass.
    const bg = scene.add
      .rectangle(r.x, r.y, r.width, r.height, 0xd6c98e)
      .setOrigin(0, 0)
      .setDepth(Z.background);
    this.container.add(bg);

    this.buildSprites();
    this.buildObstacleSprites();

    this.pointerHandler = (pointer: Phaser.Input.Pointer): void => {
      const cell = this.cellAt(pointer.worldX, pointer.worldY);
      if (cell >= 0) onCellTap(cell);
    };
    scene.input.on('pointerdown', this.pointerHandler);
  }

  private buildObstacleSprites(): void {
    for (const obs of this.board.obstacles) {
      this.addObstacleSprite(obs);
    }
  }

  // Build and animate the sprite for a single obstacle. Public so callers
  // can spawn obstacles at runtime (e.g. the H debug key) without rebuilding
  // the whole BoardView.
  addObstacleSprite(obs: Obstacle): void {
    const { x, y } = this.cellCenter(obs.index);
    const cx = obs.widthInSquares === 2 ? x + this.cellWidth / 2 : x;
    const cy = obs.heightInSquares === 2 ? y + this.cellHeight / 2 : y;
    const atlasKey = obs.spec.atlasName;
    const texture = this.scene.textures.get(atlasKey);
    if (!texture || texture.key === '__MISSING') return;
    const frames = texture
      .getFrameNames()
      .filter((n) => /^frame\d+(\.png)?$/.test(n))
      .sort((a, b) => {
        const na = Number(a.match(/^frame(\d+)/)?.[1] ?? 0);
        const nb = Number(b.match(/^frame(\d+)/)?.[1] ?? 0);
        return na - nb;
      });
    if (frames.length === 0) return;

    const sprite = this.scene.add
      .sprite(cx, cy, atlasKey, frames[0]!)
      .setDisplaySize(
        this.cellWidth * obs.widthInSquares * 0.95,
        this.cellHeight * obs.heightInSquares * 0.95,
      )
      .setDepth(Z.puppySprites);
    this.container.add(sprite);

    const animKey = `obstacle.${atlasKey}`;
    if (!this.scene.anims.exists(animKey)) {
      this.scene.anims.create({
        key: animKey,
        frames: frames.map((name) => ({ key: atlasKey, frame: name })),
        frameRate: 12,
        repeat: -1,
      });
    }
    sprite.play(animKey);
  }

  private buildSprites(): void {
    for (let i = 0; i < this.board.numSquares; i++) {
      const { x, y } = this.cellCenter(i);
      const grass = this.scene.add
        .image(x, y, 'grassrect3')
        .setDisplaySize(this.cellWidth, this.cellHeight)
        .setDepth(Z.grassSprites);
      const color = this.scene.add
        .rectangle(x, y, this.cellWidth * 0.95, this.cellHeight * 0.95, 0xffffff, 0)
        .setDepth(Z.colorSprites);
      // Start with the empty-puppy sentinel texture so a never-played cell
      // can't leak Doug (or any other breed) if visibility is ever flipped
      // erroneously. Rerender will swap in the real puppy texture when a
      // puppy is placed.
      const puppy = this.scene.add
        .sprite(x, y, 'empty-puppy')
        .setDisplaySize(this.cellWidth * 0.85, this.cellHeight * 0.85)
        .setVisible(false)
        .setDepth(Z.puppySprites);
      this.container.add([grass, color, puppy]);
      this.sprites.push({ grass, color, puppy });
    }
  }

  cellCenter(index: number): { x: number; y: number } {
    const col = index % this.board.numSquaresHoriz;
    const row = Math.floor(index / this.board.numSquaresHoriz);
    return {
      x: this.originX + col * this.cellWidth + this.cellWidth / 2,
      y: this.originY + row * this.cellHeight + this.cellHeight / 2,
    };
  }

  cellAt(worldX: number, worldY: number): number {
    const col = Math.floor((worldX - this.originX) / this.cellWidth);
    const row = Math.floor((worldY - this.originY) / this.cellHeight);
    if (col < 0 || col >= this.board.numSquaresHoriz) return -1;
    if (row < 0 || row >= this.board.numSquaresVert) return -1;
    return row * this.board.numSquaresHoriz + col;
  }

  rerender(): void {
    // Diagnostic: catch the "phantom puppy" bug — if any per-cell sprite is
    // visible while holding the empty-puppy sentinel texture, something
    // flipped visibility on a cleared cell. Should never happen; if it
    // does, log it (and rerender's normal logic below will reset both
    // visibility and texture correctly).
    for (let i = 0; i < this.board.numSquares; i++) {
      const s = this.sprites[i];
      if (s?.puppy.visible && s.puppy.texture.key === 'empty-puppy') {
        console.error(`[BoardView] phantom visible at cell ${i} (empty texture but visible)`);
      }
    }

    for (let i = 0; i < this.board.numSquares; i++) {
      const sq = this.board.squareAt(i).data;
      const s = this.sprites[i]!;
      s.grass.setAlpha(sq.isClear ? GRASS_CLEARED_ALPHA : GRASS_NOT_CLEARED_ALPHA);

      if (sq.isFireHydrant) {
        // puppy0@2x.png in the puppieslg atlas is the hydrant sentinel — use
        // it directly instead of drawing a procedural shape.
        s.color.setFillStyle(0xffffff, 0);
        s.puppy.anims.stop();
        s.puppy.setVisible(true);
        s.puppy.setTexture('puppieslg', 'puppy0@2x.png');
        s.puppy.setDisplaySize(this.cellWidth * 0.85, this.cellHeight * 0.85);
        continue;
      }

      if (sq.color !== null) {
        s.color.setFillStyle(COLOR_HEX[sq.color] ?? 0xffffff, 0.85);
      } else {
        s.color.setFillStyle(0xffffff, 0);
      }

      if (sq.puppy !== null) {
        const num = puppyForId(sq.puppy).puppyNum;
        s.puppy.setVisible(true);
        // Mirror iOS: board uses the static `puppy{N}@2x.png` from puppieslg.
        // No animation — animations live only in the header active panel.
        s.puppy.anims.stop();
        s.puppy.setTexture('puppieslg', `puppy${num}@2x.png`);
        s.puppy.setDisplaySize(this.cellWidth * 0.85, this.cellHeight * 0.85);
      } else {
        s.puppy.setVisible(false);
        s.puppy.stop();
        // Reset to the sentinel so an erroneous setVisible(true) later can't
        // leak the previously-placed breed.
        s.puppy.setTexture('empty-puppy');
      }
    }
  }

  // Fade the grass back in for cells that just regrew (thunderstorm).
  // Pins each cell's grass alpha to the cleared value, then tweens to full
  // opacity over `durationMs`, so the grass appears to grow back slowly
  // rather than snapping to full green when rerender() runs.
  animateRegrow(indices: number[], durationMs: number): void {
    for (const idx of indices) {
      const s = this.sprites[idx];
      if (!s) continue;
      s.grass.setAlpha(GRASS_CLEARED_ALPHA);
      this.scene.tweens.add({
        targets: s.grass,
        alpha: GRASS_NOT_CLEARED_ALPHA,
        duration: durationMs,
        ease: 'Sine.InOut',
      });
    }
  }

  // Build the MegaPuppy 2x2 sprite. Hides the 4 underlying cell puppies.
  // SuperMegaPuppy uses the stars overlay variant. When `animate` is false,
  // the visual is built directly at rest scale (used to restore saved
  // MegaPuppies on reload — no entrance bounce, stars, or headline).
  formMegaPuppy(
    topLeftIndex: number,
    puppy: PuppyId,
    isSuper: boolean,
    onTap: () => void,
    animate = true,
  ): Phaser.GameObjects.Sprite {
    const tl = this.cellCenter(topLeftIndex);
    const br = this.cellCenter(topLeftIndex + 1 + this.board.numSquaresHoriz);
    const cx = (tl.x + br.x) / 2;
    const cy = (tl.y + br.y) / 2;

    for (const idx of [
      topLeftIndex,
      topLeftIndex + 1,
      topLeftIndex + this.board.numSquaresHoriz,
      topLeftIndex + 1 + this.board.numSquaresHoriz,
    ]) {
      this.sprites[idx]?.puppy.setVisible(false);
    }

    // Red MegaPuppy border. The crazysquareframe asset is 73×73 with the red
    // FILL in the top-left 68×68 — the bottom-right 5px is a drop shadow we
    // crop out (setCrop), anchored at the red fill's centre (pixel 34, 34)
    // so the red region is centred on (cx, cy). FRAME_OUTER sets the outer
    // edge of the red — just inside the 2×2 cell boundary. INNER sets the
    // bg + mask bounds (smaller than the frame) so the red ring is visible.
    const FRAME_OUTER = 0.97;
    const INNER = 0.93;
    const borderSize = this.cellWidth * 2 * FRAME_OUTER * (73 / 68);
    const border = this.scene.add
      .image(cx, cy, 'crazysquareframe')
      .setOrigin(34 / 73, 34 / 73)
      .setDisplaySize(borderSize, borderSize)
      .setCrop(0, 0, 68, 68)
      .setDepth(Z.megaPuppyExplosion)
      .setAlpha(0);

    const bgKey = isSuper ? 'crazysquarelg2wstars' : 'crazysquarelg2';
    // Bg sits between the border and the puppy/stars so the puppy + spinning
    // stars are clearly in front of the crazysquare graphic. It rotates
    // continuously and is masked to INNER × the 2×2 box, so it must be sized
    // large enough that its inscribed square (at 45°) still fills the masked
    // area — i.e. ≥ √2 × side. 1.45 gives a small safety margin.
    const BG_OVERSCAN = 1.45;
    const bg = this.scene.add
      .image(cx, cy, bgKey)
      .setDisplaySize(this.cellWidth * 2 * INNER * BG_OVERSCAN, this.cellHeight * 2 * INNER * BG_OVERSCAN)
      .setDepth(Z.megaPuppyExplosion + 1)
      .setScale(animate ? 0.3 : 1);

    const num = puppyForId(puppy).puppyNum;
    // Use pNa1/frame1 (the anim-1 first-frame) as the still texture rather
    // than puppieslg/puppyN@2x.png, so the still ↔ anim transition is
    // seamless. puppieslg has different padding around the dog than the
    // anim atlases, which would cause a visible jump every time an idle
    // anim starts or ends. Mirrors ColorPickerView.resetToStatic.
    const stillAtlas = `p${num}a1`;
    const stillFrameName = 'frame1';
    const hasStillAtlas = this.scene.textures.exists(stillAtlas);
    const initialTextureKey = hasStillAtlas ? stillAtlas : 'puppieslg';
    const initialFrameName = hasStillAtlas ? stillFrameName : `puppy${num}@2x.png`;
    const sprite = this.scene.add
      .sprite(cx, cy, initialTextureKey, initialFrameName)
      .setDepth(Z.megaPuppySprite)
      .setAlpha(animate ? 0 : 1);
    // Only SuperMegaPuppies are tappable — they convert to a CrazySquare.
    // A plain MegaPuppy (4 same breed, mixed colours) is decorative only.
    if (isSuper) {
      sprite.setInteractive({ useHandCursor: true });
      sprite.on('pointerdown', onTap);
    }
    if (animate) bg.setAlpha(0);

    // Target scales = the size that fits inside the 2×2 cell box. The
    // bounce keyframes are MULTIPLIERS of these, so the final rest scale
    // is correct (sprite fits the box) rather than 1× native ~182px.
    const targetSpriteW = this.cellWidth * 2 * 0.85;
    const targetSpriteH = this.cellHeight * 2 * 0.85;
    // Uniform per-puppy scale (PUPPY_MAX_DIM-based) so anim frames keep
    // their natural aspect during animation, mirroring ColorPickerView.
    const megaTargetSize = targetSpriteW;
    const animScale = megaTargetSize / (PUPPY_MAX_DIM[String(num)] ?? 200);
    const targetBgW = this.cellWidth * 2 * INNER * BG_OVERSCAN;
    const targetBgH = this.cellHeight * 2 * INNER * BG_OVERSCAN;
    const bgScaleX = targetBgW / bg.width;
    const bgScaleY = targetBgH / bg.height;
    void targetSpriteH;

    // Set the still anchor + offset on the sprite. Used at rest (initial
    // load, after bounce-in, and between idle anims) so transitions to/from
    // an idle animation are seamless.
    const applyStillState = (): void => {
      if (hasStillAtlas) {
        sprite.setTexture(stillAtlas, stillFrameName);
        const a = PUPPY_ANCHORS[stillAtlas]?.[stillFrameName] ?? { x: 0.5, y: 0.5 };
        sprite.setOrigin(a.x, a.y);
      } else {
        sprite.setOrigin(0.5, 0.5);
      }
      const offset = alignmentForStill(puppy, megaTargetSize);
      sprite.setPosition(cx + offset.dx, cy + offset.dy);
      sprite.setScale(animScale);
    };

    // Crop the MegaPuppy sprite AND the rotating bg to the INNER region so
    // the red frame is visible around them and rotation overscan stays inside.
    const maskW = this.cellWidth * 2 * INNER;
    const maskH = this.cellHeight * 2 * INNER;
    const maskG = this.scene.make.graphics({ x: 0, y: 0 });
    maskG.fillStyle(0xffffff, 1);
    maskG.fillRect(cx - maskW / 2, cy - maskH / 2, maskW, maskH);
    maskG.setVisible(false);
    const geomMask = maskG.createGeometryMask();
    sprite.setMask(geomMask);
    bg.setMask(geomMask);

    if (animate) {
      // Megastars spinning overlay — appears on top, scales up while spinning
      // 900° over ~2.5s, then fades. Mirrors GameScene.swift::starsSequence.
      const starsSize = this.cellWidth * 2 * 1.2;
      const stars = this.scene.add
        .image(cx, cy, 'megastars')
        .setDisplaySize(starsSize, starsSize)
        .setDepth(Z.circleOfStars)
        .setOrigin(0.5)
        .setScale(0.25)
        .setAlpha(0.9);

      this.scene.tweens.add({
        targets: stars,
        scale: 1.0,
        rotation: Math.PI * 5,
        duration: 2500,
        ease: 'Linear',
        onComplete: () => {
          this.scene.tweens.add({
            targets: stars,
            alpha: 0,
            duration: 250,
            onComplete: () => stars.destroy(),
          });
        },
      });

      // Sprite bounce: scale ramps as multipliers of the 2×2-fit scale so
      // the final rest size is correct. The puppy stays upright — only the
      // background graphic spins. Apply still anchor + offset before the
      // bounce so it pivots around the same point the idle anims will.
      applyStillState();
      sprite.setScale(0).setRotation(0);
      this.scene.tweens.chain({
        targets: sprite,
        tweens: [
          { alpha: 1, scaleX: animScale * 1.5, scaleY: animScale * 1.5, duration: 350, ease: 'Sine.Out' },
          { scaleX: animScale * 0.5, scaleY: animScale * 0.5, duration: 200, ease: 'Sine.InOut' },
          { scaleX: animScale * 1.2, scaleY: animScale * 1.2, duration: 250, ease: 'Sine.Out' },
          { scaleX: animScale * 0.9, scaleY: animScale * 0.9, duration: 300, ease: 'Sine.InOut' },
          { scaleX: animScale,       scaleY: animScale,       duration: 450, ease: 'Sine.InOut' },
        ],
      });
      // Background bounce — same shape, no rotation (rotation handled separately).
      bg.setScale(0);
      this.scene.tweens.chain({
        targets: bg,
        tweens: [
          { alpha: 1, scaleX: bgScaleX * 1.5, scaleY: bgScaleY * 1.5, duration: 350, ease: 'Sine.Out' },
          { scaleX: bgScaleX * 0.5, scaleY: bgScaleY * 0.5, duration: 200, ease: 'Sine.InOut' },
          { scaleX: bgScaleX * 1.2, scaleY: bgScaleY * 1.2, duration: 250, ease: 'Sine.Out' },
          { scaleX: bgScaleX * 0.9, scaleY: bgScaleY * 0.9, duration: 300, ease: 'Sine.InOut' },
          { scaleX: bgScaleX,       scaleY: bgScaleY,       duration: 450, ease: 'Sine.InOut' },
        ],
      });
      // Border fades in alongside the bg bounce.
      this.scene.tweens.add({
        targets: border,
        alpha: 1,
        duration: 350,
        ease: 'Sine.Out',
      });

      // "MEGAPUPPY!" headline — centered over the 4-square box, scales up
      // with a multi-step bounce, settles, then fades out.
      const headline = this.scene.add
        .image(cx, cy, 'megapuppy')
        .setOrigin(0.5)
        .setDepth(Z.plus100)
        .setAlpha(0)
        .setScale(0);
      const headlineTargetW = this.cellWidth * 2 * 0.95;
      const headlineScale = headlineTargetW / headline.width;
      this.scene.tweens.chain({
        targets: headline,
        tweens: [
          { alpha: 1, scaleX: headlineScale * 1.5, scaleY: headlineScale * 1.5, duration: 280, ease: 'Sine.Out' },
          { scaleX: headlineScale * 0.6, scaleY: headlineScale * 0.6, duration: 180, ease: 'Sine.InOut' },
          { scaleX: headlineScale * 1.25, scaleY: headlineScale * 1.25, duration: 200, ease: 'Sine.Out' },
          { scaleX: headlineScale * 0.9, scaleY: headlineScale * 0.9, duration: 180, ease: 'Sine.InOut' },
          { scaleX: headlineScale, scaleY: headlineScale, duration: 220, ease: 'Sine.InOut' },
          { delay: 900, alpha: 0, duration: 400, ease: 'Linear',
            onComplete: () => headline.destroy() },
        ],
      });
    } else {
      // Restore mode: skip entrance animations — settle directly at rest.
      applyStillState();
      bg.setScale(bgScaleX, bgScaleY);
      border.setAlpha(1);
    }

    // Continuous rotation of the crazysquare image while shown (formed or
    // restored).
    this.scene.tweens.add({
      targets: bg,
      rotation: Math.PI * 2,
      duration: 4000,
      ease: 'Linear',
      repeat: -1,
    });

    // Random idle animation every 8s. Mirrors iOS boardDoUpdate
    // (Board.swift:986-1005) which calls megaPuppyRandomAnimation on each
    // MegaPuppy whose lastAnimated is older than 8s. We schedule per-mega
    // timers rather than a shared update loop. During the anim we switch to
    // a per-frame nose-anchor + alignment-offset model (same as
    // ColorPickerView) so frames don't jump around as their padding changes.
    const applyAnimFrame = (atlasKey: string, frameName: string, animIdx: number): void => {
      const a = PUPPY_ANCHORS[atlasKey]?.[frameName] ?? { x: 0.5, y: 0.5 };
      sprite.setScale(animScale);
      sprite.setOrigin(a.x, a.y);
      const offset = alignmentForAnim(puppy, animIdx, megaTargetSize);
      sprite.setPosition(cx + offset.dx, cy + offset.dy);
    };
    const playIdleAnim = (): void => {
      if (!sprite.active) return;
      if (sprite.anims.isPlaying) return;
      const animKey = pickIdleAnimKey(num, (max) => Math.floor(Math.random() * max));
      if (!this.scene.anims.exists(animKey)) return;
      const atlasKey = animKey.replace(/^puppy(\d+)\.anim(\d+)$/, 'p$1a$2');
      const animNum = Number(animKey.match(/anim(\d+)$/)?.[1] ?? 1);
      const animIdx = animNum - 1;
      sprite.off('animationupdate');
      sprite.on(
        'animationupdate',
        (_anim: Phaser.Animations.Animation, frame: Phaser.Animations.AnimationFrame) => {
          applyAnimFrame(atlasKey, frame.textureFrame as string, animIdx);
        },
      );
      sprite.play({ key: animKey, repeat: 0 });
      const firstFrame = this.scene.anims.get(animKey)?.frames[0]?.textureFrame as
        | string
        | undefined;
      if (firstFrame) applyAnimFrame(atlasKey, firstFrame, animIdx);
      sprite.once('animationcomplete', () => {
        if (!sprite.active) return;
        sprite.off('animationupdate');
        // Back to rest using the still anchor + offset so the transition
        // out of the anim is seamless.
        applyStillState();
      });
    };
    const idleTimer = this.scene.time.addEvent({
      delay: 8000,
      loop: true,
      callback: playIdleAnim,
    });

    this.megaVisuals.set(topLeftIndex, { border, bg, sprite, idleTimer, maskG });
    return sprite;
  }

  // Play a one-shot sparkle on the given cell. Mirrors iOS
  // Square.swift::clearAndSparkleSquare's stardust trail when a row/col
  // clear wipes a cell. Multiple cells can sparkle in parallel — each
  // owns its own sprite which self-destroys when the anim completes.
  sparkleAt(cellIndex: number): void {
    const { x, y } = this.cellCenter(cellIndex);
    const sparkleKey = 'sparkle.spin';
    if (!this.scene.anims.exists(sparkleKey)) {
      // 8 frames at 20 fps → 0.4 s per cycle. We then repeat it ~3.75×
      // (config = 3 additional repeats after the first play) to keep the
      // total dwell at ≈ 1.5 s while letting the sparkle spin visibly fast.
      this.scene.anims.create({
        key: sparkleKey,
        frames: Array.from({ length: 8 }, (_, i) => ({
          key: 'sparklespin',
          frame: `sparkspin${i + 1}@2x.png`,
        })),
        frameRate: 20,
        repeat: 0,
      });
    }
    const size = this.cellWidth * 0.9;
    const spark = this.scene.add
      .sprite(x, y, 'sparklespin', 'sparkspin1@2x.png')
      .setDisplaySize(size, size)
      .setDepth(Z.sparkleSprites)
      .setOrigin(0.5);
    spark.play({ key: sparkleKey, repeat: 3 });
    spark.once('animationcomplete', () => spark.destroy());
  }

  // Destroy the visual artifacts (border, bg, sprite, timer) for the
  // MegaPuppy at `topLeftIndex`. Used when a row/col clear removes it from
  // the board, and when a SuperMega is converted to a CrazySquare.
  destroyMegaVisual(topLeftIndex: number): void {
    const v = this.megaVisuals.get(topLeftIndex);
    if (!v) return;
    v.idleTimer.remove(false);
    v.border.destroy();
    v.bg.destroy();
    v.sprite.destroy();
    v.maskG.destroy();
    this.megaVisuals.delete(topLeftIndex);

    // Defensive: hide every per-cell puppy sprite that sat under this mega
    // AND reset it to the empty-puppy sentinel texture. If some other path
    // later flips its visibility back to true (the "phantom puppy" bug),
    // the sentinel renders nothing — and rerender's diagnostic will log it.
    const horiz = this.board.numSquaresHoriz;
    for (const idx of [
      topLeftIndex,
      topLeftIndex + 1,
      topLeftIndex + horiz,
      topLeftIndex + 1 + horiz,
    ]) {
      const s = this.sprites[idx];
      if (!s) continue;
      s.puppy.setVisible(false);
      s.puppy.anims.stop();
      s.puppy.setTexture('empty-puppy');
    }
  }

  // Unconditional clear of the per-cell puppy sprite. Used by the SuperMega
  // tap handler so no stale puppy sprite remains visible at one of the 4
  // cells under the SuperMega that was just converted to a CrazySquare.
  forceClearCellSprite(cellIndex: number): void {
    const s = this.sprites[cellIndex];
    if (!s) return;
    s.puppy.anims.stop();
    s.puppy.setVisible(false);
    s.puppy.setTexture('empty-puppy');
  }

  animatePlacement(cellIndex: number): void {
    const s = this.sprites[cellIndex];
    if (!s) return;
    s.puppy.setVisible(true);
    // The bounce keyframes are multipliers of the final cell-fit scale, so
    // the sprite settles back at the same size buildSprites set it to
    // (cellWidth * 0.85). Without this the sprite would settle at scale 1
    // of the native ~182px texture and overflow the cell.
    const targetW = this.cellWidth * 0.85;
    const targetH = this.cellHeight * 0.85;
    const baseSX = targetW / s.puppy.width;
    const baseSY = targetH / s.puppy.height;
    s.puppy.setScale(0);
    // Ports Square.swift L773-777: a 5-step bounce-and-settle.
    this.scene.tweens.chain({
      targets: s.puppy,
      tweens: [
        { scaleX: baseSX * 1.5, scaleY: baseSY * 1.5, duration: 100, ease: 'Sine.Out' },
        { scaleX: baseSX * 0.5, scaleY: baseSY * 0.5, duration: 150, ease: 'Sine.InOut' },
        { scaleX: baseSX * 1.2, scaleY: baseSY * 1.2, duration: 190, ease: 'Sine.Out' },
        { scaleX: baseSX * 0.9, scaleY: baseSY * 0.9, duration: 200, ease: 'Sine.InOut' },
        { scaleX: baseSX,       scaleY: baseSY,       duration: 210, ease: 'Sine.InOut' },
      ],
    });
  }

  private hintSprite: Phaser.GameObjects.Image | null = null;
  private hintTween: Phaser.Tweens.Tween | Phaser.Tweens.TweenChain | null = null;

  showHint(cellIndex: number): void {
    const { x, y } = this.cellCenter(cellIndex);
    // iOS uses bestsquarec.png (a purple paw) centered on the cell with
    // anchorPoint=ptCenter, then runs a fade+scale+spin+bounce sequence to
    // call attention to it. See Hints.swift::animateBestSquare.
    const targetSize = this.cellWidth * 0.85;
    if (!this.hintSprite) {
      this.hintSprite = this.scene.add
        .image(x, y, 'bestsquarec')
        .setOrigin(0.5)
        .setDisplaySize(targetSize, targetSize)
        .setDepth(Z.bestSquare);
      this.container.add(this.hintSprite);
    } else {
      this.hintSprite
        .setPosition(x, y)
        .setDisplaySize(targetSize, targetSize)
        .setVisible(true)
        .setRotation(0)
        .setAlpha(0);
    }

    this.hintTween?.stop();
    const sprite = this.hintSprite;
    sprite.setAlpha(0).setScale(1).setRotation(0);

    // Ports animateBestSquare's sequence:
    //   fadeUp(0→1, 0.75s) → scale 1.5 (0.1s) → group{scale 0.5, rotate 360°}
    //   → scale 1.2 (0.25s) → scale 0.9 (0.3s) → scale 1.0 (0.45s) → loop
    // We omit the trailing fadeOut+kidDelay and loop forever so the hint
    // stays visible while it's the user's turn.
    const baseScaleX = sprite.scaleX;
    const baseScaleY = sprite.scaleY;
    this.hintTween = this.scene.tweens.chain({
      targets: sprite,
      loop: -1,
      tweens: [
        { alpha: 1, duration: 750, ease: 'Linear' },
        { scaleX: baseScaleX * 1.5, scaleY: baseScaleY * 1.5, duration: 100, ease: 'Sine.InOut' },
        {
          scaleX: baseScaleX * 0.5,
          scaleY: baseScaleY * 0.5,
          rotation: Math.PI * 2,
          duration: 500,
          ease: 'Sine.InOut',
          onComplete: () => sprite.setRotation(0),
        },
        { scaleX: baseScaleX * 1.2, scaleY: baseScaleY * 1.2, duration: 250, ease: 'Sine.Out' },
        { scaleX: baseScaleX * 0.9, scaleY: baseScaleY * 0.9, duration: 300, ease: 'Sine.InOut' },
        { scaleX: baseScaleX,       scaleY: baseScaleY,       duration: 450, ease: 'Sine.InOut' },
        { duration: 800 },
      ],
    });
  }

  hideHint(): void {
    this.hintTween?.stop();
    this.hintTween = null;
    this.hintSprite?.setVisible(false);
  }

  // Immediately stop and hide the onboarding paw (rather than letting it finish
  // its fade). Used once onboarding ends so the paw clears the moment the 5th
  // puppy is placed.
  hideOnboardingPaw(): void {
    this.onboardPawTween?.stop();
    this.onboardPawTween = null;
    this.onboardPawSprite?.setVisible(false);
  }

  // Onboarding paw that draws attention to the best playable square for the
  // first few turns (non-Kid Mode). Reuses the Kid-Mode paw texture and its
  // fade→scale→spin→bounce sequence, but runs a fixed number of cycles then
  // fades out instead of looping forever. Kept on its own sprite/tween
  // (separate from the always-on Kid-Mode paw in hintSprite) so that
  // updateHint()'s hideHint() in non-Kid Mode can't tear it down mid-pulse.
  private onboardPawSprite: Phaser.GameObjects.Image | null = null;
  private onboardPawTween: Phaser.Tweens.Tween | Phaser.Tweens.TweenChain | null = null;

  animateHintPaw(cellIndex: number, cycles: number): void {
    const { x, y } = this.cellCenter(cellIndex);
    const targetSize = this.cellWidth * 0.85;
    if (!this.onboardPawSprite) {
      this.onboardPawSprite = this.scene.add
        .image(x, y, 'bestsquarec')
        .setOrigin(0.5)
        .setDisplaySize(targetSize, targetSize)
        .setDepth(Z.bestSquare + 1);
      this.container.add(this.onboardPawSprite);
    } else {
      this.onboardPawSprite
        .setPosition(x, y)
        .setDisplaySize(targetSize, targetSize)
        .setVisible(true);
    }
    this.onboardPawTween?.stop();
    const sprite = this.onboardPawSprite;
    sprite.setAlpha(0).setScale(1).setRotation(0);
    const baseScaleX = sprite.scaleX;
    const baseScaleY = sprite.scaleY;
    this.onboardPawTween = this.scene.tweens.chain({
      targets: sprite,
      repeat: cycles - 1,
      onComplete: () => {
        this.scene.tweens.add({
          targets: sprite,
          alpha: 0,
          duration: 300,
          onComplete: () => sprite.setVisible(false),
        });
      },
      tweens: [
        { alpha: 1, duration: 750, ease: 'Linear' },
        { scaleX: baseScaleX * 1.5, scaleY: baseScaleY * 1.5, duration: 100, ease: 'Sine.InOut' },
        {
          scaleX: baseScaleX * 0.5,
          scaleY: baseScaleY * 0.5,
          rotation: Math.PI * 2,
          duration: 500,
          ease: 'Sine.InOut',
          onComplete: () => sprite.setRotation(0),
        },
        { scaleX: baseScaleX * 1.2, scaleY: baseScaleY * 1.2, duration: 250, ease: 'Sine.Out' },
        { scaleX: baseScaleX * 0.9, scaleY: baseScaleY * 0.9, duration: 300, ease: 'Sine.InOut' },
        { scaleX: baseScaleX,       scaleY: baseScaleY,       duration: 450, ease: 'Sine.InOut' },
        { duration: 800 },
      ],
    });
  }

  // Full teardown — used by Replay Level, which replaces the BoardView
  // wholesale. Mega visuals are created via scene.add (NOT inside the
  // container), so destroying the container alone doesn't reach them; we
  // have to walk the map and destroy each set explicitly. The scene-level
  // pointerdown listener is also detached so the old BoardView no longer
  // intercepts cell taps after Replay.
  destroy(): void {
    this.scene.input.off('pointerdown', this.pointerHandler);
    for (const [, v] of this.megaVisuals) {
      v.idleTimer.remove(false);
      v.border.destroy();
      v.bg.destroy();
      v.sprite.destroy();
    }
    this.megaVisuals.clear();
    this.hintTween?.stop();
    this.hintTween = null;
    this.hintSprite?.destroy();
    this.hintSprite = null;
    this.onboardPawTween?.stop();
    this.onboardPawTween = null;
    this.onboardPawSprite?.destroy();
    this.onboardPawSprite = null;
    this.container.destroy(true);
  }
}
